export type StoreItemInstance<TReturn> = {
  data: TReturn
  version: number
}

export const createExternalStore = <TReturn>() => {
  const instances = new Map<string, StoreItemInstance<TReturn>>()
  const subscribers = new Set<() => void>()

  const subscribe = (callback: () => void): (() => void) => {
    subscribers.add(callback)
    return () => {
      subscribers.delete(callback)
    }
  }

  const getSnapshot = (id: string): TReturn | undefined => {
    return instances.get(id)?.data
  }

  const updateInstance = (id: string, data: TReturn): void => {
    const current = instances.get(id)
    const version = (current?.version ?? 0) + 1

    instances.set(id, { data, version })
    notifySubscribers()
  }

  const updateProperty = (id: string, property: keyof TReturn, value: TReturn[keyof TReturn]): void => {
    const current = instances.get(id)
    const version = (current?.version ?? 0) + 1
    instances.set(id, { data: { ...(current?.data ?? {}), [property]: value } as TReturn, version })
    notifySubscribers()
  }

  const hasInstance = (id: string): boolean => {
    return instances.has(id)
  }

  const getInstances = (): Map<string, StoreItemInstance<TReturn>> => {
    return instances
  }

  const notifySubscribers = (): void => {
    subscribers.forEach(callback => callback())
  }

  return {
    subscribe,
    getSnapshot,
    updateInstance,
    getInstances,
    updateProperty,
    hasInstance,
  }
}
